import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PageSkeleton } from './components/ui/Skeleton';

// ─── Lazy-loaded pages (code splitting) ─────────────────────────────────────

const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const PlaceholderPage = lazy(() => import('./pages/PlaceholderPage').then(m => ({ default: m.PlaceholderPage })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AccountPortfolio = lazy(() => import('./pages/AccountPortfolio').then(m => ({ default: m.AccountPortfolio })));
const EditSite = lazy(() => import('./pages/EditSite').then(m => ({ default: m.EditSite })));
const Alerts = lazy(() => import('./pages/Alerts').then(m => ({ default: m.Alerts })));
const Maintenance = lazy(() => import('./pages/Maintenance').then(m => ({ default: m.Maintenance })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const Groups = lazy(() => import('./pages/Groups').then(m => ({ default: m.Groups })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));
const ServerError = lazy(() => import('./pages/ServerError').then(m => ({ default: m.ServerError })));

// Site tabs
const SiteTabNav = lazy(() => import('./components/layout/SiteTabNav').then(m => ({ default: m.SiteTabNav })));
// ENER-225 T1: the overview route is a classification gate — battery-only sites
// get BessOverview, everything else keeps OverviewTab.
const OverviewRoute = lazy(() => import('./pages/site/OverviewRoute').then(m => ({ default: m.OverviewRoute })));
const SiteIndexRedirect = lazy(() => import('./pages/site/SiteIndexRedirect').then(m => ({ default: m.SiteIndexRedirect })));
const AnalysisTab = lazy(() => import('./pages/site/AnalysisTab').then(m => ({ default: m.AnalysisTab })));
const MathTab = lazy(() => import('./pages/site/MathTab').then(m => ({ default: m.MathTab })));
const EmsTab = lazy(() => import('./pages/site/EmsTab').then(m => ({ default: m.EmsTab })));
const DocumentsTab = lazy(() => import('./pages/site/DocumentsTab').then(m => ({ default: m.DocumentsTab })));
const DataExplorerTab = lazy(() => import('./pages/site/DataExplorerTab').then(m => ({ default: m.DataExplorerTab })));
const MaintenanceTab = lazy(() => import('./pages/site/MaintenanceTab').then(m => ({ default: m.MaintenanceTab })));
const FinancialsTab = lazy(() => import('./pages/site/FinancialsTab').then(m => ({ default: m.FinancialsTab })));
const SiteReportsTab = lazy(() => import('./pages/site/SiteReportsTab').then(m => ({ default: m.SiteReportsTab })));
const SiteAlertsTab = lazy(() => import('./pages/site/AlertsTab'));
const InverterFleetPage = lazy(() => import('./pages/site/InverterFleetPage').then(m => ({ default: m.InverterFleetPage })));
const InverterDetailPage = lazy(() => import('./pages/site/InverterDetailPage').then(m => ({ default: m.InverterDetailPage })));

// Admin pages
const AdminAccounts = lazy(() => import('./pages/admin/Accounts').then(m => ({ default: m.AdminAccounts })));
const AdminUsers = lazy(() => import('./pages/admin/Users').then(m => ({ default: m.AdminUsers })));
const ThemeEditor = lazy(() => import('./pages/admin/ThemeEditor').then(m => ({ default: m.ThemeEditor })));
const AdminBackfills = lazy(() => import('./pages/admin/Backfills').then(m => ({ default: m.AdminBackfills })));
const AdminWorkers = lazy(() => import('./pages/admin/Workers').then(m => ({ default: m.AdminWorkers })));
// ENER-118: WAPI Compare now hosts BOTH single-site and fleet modes via an
// internal mode toggle (?mode=fleet). No separate fleet route — the page
// imports FleetCompareView directly.
const AdminWapiCompare = lazy(() => import('./pages/admin/WapiCompare').then(m => ({ default: m.WapiCompare })));
const AdminContactMessages = lazy(() => import('./pages/admin/ContactMessages').then(m => ({ default: m.ContactMessages })));
const AdminSystemSettings = lazy(() => import('./pages/admin/SystemSettings').then(m => ({ default: m.AdminSystemSettings })));
const AdminOemConnections = lazy(() => import('./pages/admin/OemConnections').then(m => ({ default: m.AdminOemConnections })));

// Public auth recovery pages
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const SetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.SetPassword })));
const UserSiteAccess = lazy(() => import('./pages/UserSiteAccess').then(m => ({ default: m.UserSiteAccess })));

// ─── Suspense wrapper ────────────────────────────────────────────────────────

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

// ─── Route protection wrapper ────────────────────────────────────────────────

function Protected({ children, minimumRole }: { children: React.ReactNode; minimumRole?: 'master' | 'account_manager' | 'technician' | 'viewer' }) {
  return <ProtectedRoute minimumRole={minimumRole}>{children}</ProtectedRoute>;
}

// ─── Router ──────────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  // Public routes
  { path: '/login', element: <SuspenseWrap><LoginPage /></SuspenseWrap> },
  { path: '/forgot-password', element: <SuspenseWrap><ForgotPassword /></SuspenseWrap> },
  { path: '/reset-password/:token', element: <SuspenseWrap><ResetPassword /></SuspenseWrap> },
  { path: '/set-password/:token', element: <SuspenseWrap><SetPassword /></SuspenseWrap> },

  // Error pages
  { path: '/500', element: <SuspenseWrap><ServerError /></SuspenseWrap> },

  // Protected routes (all authenticated)
  {
    path: '/',
    element: (
      <Protected>
        <DashboardLayout />
      </Protected>
    ),
    children: [
      { index: true, element: <SuspenseWrap><Dashboard /></SuspenseWrap> },
      { path: 'account/:accountId', element: <SuspenseWrap><AccountPortfolio /></SuspenseWrap> },
      { path: 'sites', element: <SuspenseWrap><PlaceholderPage title="Sites" /></SuspenseWrap> },

      // Site deep dive with 6 tabs
      {
        path: 'site/:siteId',
        element: <SuspenseWrap><SiteTabNav /></SuspenseWrap>,
        children: [
          // EMS sites land on EMS, everything else on Overview.
          { index: true, element: <SuspenseWrap><SiteIndexRedirect /></SuspenseWrap> },
          { path: 'overview', element: <SuspenseWrap><OverviewRoute /></SuspenseWrap> },
          { path: 'inverters', element: <SuspenseWrap><InverterFleetPage /></SuspenseWrap> },
          { path: 'analysis', element: <SuspenseWrap><AnalysisTab /></SuspenseWrap> },
          { path: 'ems', element: <SuspenseWrap><EmsTab /></SuspenseWrap> },
          { path: 'math', element: <SuspenseWrap><MathTab /></SuspenseWrap> },
          { path: 'financials', element: <SuspenseWrap><FinancialsTab /></SuspenseWrap> },
          { path: 'documents', element: <SuspenseWrap><DocumentsTab /></SuspenseWrap> },
          { path: 'alerts', element: <SuspenseWrap><SiteAlertsTab /></SuspenseWrap> },
          { path: 'data-explorer', element: <SuspenseWrap><DataExplorerTab /></SuspenseWrap> },
          { path: 'reports', element: <SuspenseWrap><SiteReportsTab /></SuspenseWrap> },
          { path: 'maintenance', element: <SuspenseWrap><MaintenanceTab /></SuspenseWrap> },
        ],
      },

      { path: 'site/:siteId/inverter/:serial', element: <SuspenseWrap><InverterDetailPage /></SuspenseWrap> },

      {
        path: 'site/:siteId/edit',
        element: (
          <Protected minimumRole="account_manager">
            <SuspenseWrap><EditSite /></SuspenseWrap>
          </Protected>
        ),
      },
      { path: 'inverters/:siteId', element: <SuspenseWrap><PlaceholderPage title="Inverter Analytics" /></SuspenseWrap> },
      { path: 'alerts', element: <SuspenseWrap><Alerts /></SuspenseWrap> },
      { path: 'maintenance', element: <SuspenseWrap><Maintenance /></SuspenseWrap> },
      { path: 'reports', element: <SuspenseWrap><Reports /></SuspenseWrap> },
      { path: 'groups', element: <SuspenseWrap><Groups /></SuspenseWrap> },
      { path: 'settings', element: <SuspenseWrap><Settings /></SuspenseWrap> },
      {
        path: 'user-site-access',
        element: (
          <Protected minimumRole="account_manager">
            <SuspenseWrap><UserSiteAccess /></SuspenseWrap>
          </Protected>
        ),
      },

      // Admin routes (master only)
      {
        path: 'admin/accounts',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminAccounts /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <Protected minimumRole="account_manager">
            <SuspenseWrap><AdminUsers /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/accounts/:accountId/theme',
        element: (
          <Protected minimumRole="account_manager">
            <SuspenseWrap><ThemeEditor /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/backfills',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminBackfills /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/contact-messages',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminContactMessages /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/workers',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminWorkers /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/wapi-compare',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminWapiCompare /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/wapi-compare/:siteId',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminWapiCompare /></SuspenseWrap>
          </Protected>
        ),
      },
      // ENER-118: the old standalone fleet route now redirects into the
      // unified Compare page's fleet mode. Preserves any saved bookmarks.
      {
        path: 'admin/wapi-compare-fleet',
        element: <Navigate to="/admin/wapi-compare?mode=fleet" replace />,
      },
      {
        path: 'admin/settings',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminSystemSettings /></SuspenseWrap>
          </Protected>
        ),
      },
      {
        path: 'admin/oem-connections',
        element: (
          <Protected minimumRole="master">
            <SuspenseWrap><AdminOemConnections /></SuspenseWrap>
          </Protected>
        ),
      },

      // 404 catch-all (inside dashboard layout)
      { path: '*', element: <SuspenseWrap><NotFound /></SuspenseWrap> },
    ],
  },

  // Global 404 catch-all (outside dashboard layout)
  { path: '*', element: <SuspenseWrap><NotFound /></SuspenseWrap> },
]);
